"use client";

import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState
} from "react";

interface ContentNavigationContextType {
    // 현재 탐색 가능한 콘텐츠 목록
    contentList: Mix_ContentFragment[];
    // 현재 선택된 콘텐츠의 인덱스
    currentIndex: number;
    // 콘텐츠 목록 설정 (모달 열릴 때 호출)
    setContentList: (
        contents: Mix_ContentFragment[],
        currentId: string
    ) => void;
    // 콘텐츠 목록 초기화 (모달 닫힐 때 호출)
    clearContentList: () => void;
    // 이전/다음 콘텐츠로 이동
    navigateTo: (direction: "prev" | "next") => string | null;
    // 이전 콘텐츠 이동 가능 여부
    canNavigatePrev: boolean;
    // 다음 콘텐츠 이동 가능 여부
    canNavigateNext: boolean;
    // 현재 콘텐츠
    currentContent: Mix_ContentFragment | null;
}

const ContentNavigationContext = createContext<ContentNavigationContextType>({
    contentList: [],
    currentIndex: -1,
    setContentList: () => {},
    clearContentList: () => {},
    navigateTo: () => null,
    canNavigatePrev: false,
    canNavigateNext: false,
    currentContent: null
});

export const ContentNavigationProvider = ({
    children
}: {
    children: React.ReactNode;
}) => {
    const [contentList, setContentListState] = useState<Mix_ContentFragment[]>(
        []
    );
    const [currentIndex, setCurrentIndex] = useState<number>(-1);

    const setContentList = useCallback(
        (contents: Mix_ContentFragment[], currentId: string) => {
            setContentListState(contents);
            const index = contents.findIndex((c) => c.id === currentId);
            setCurrentIndex(index);
        },
        []
    );

    const clearContentList = useCallback(() => {
        setContentListState([]);
        setCurrentIndex(-1);
    }, []);

    const canNavigatePrev = useMemo(() => currentIndex > 0, [currentIndex]);

    const canNavigateNext = useMemo(
        () => currentIndex >= 0 && currentIndex < contentList.length - 1,
        [currentIndex, contentList.length]
    );

    const currentContent = useMemo(
        () => (currentIndex >= 0 ? (contentList[currentIndex] ?? null) : null),
        [contentList, currentIndex]
    );

    const navigateTo = useCallback(
        (direction: "prev" | "next"): string | null => {
            if (direction === "prev" && canNavigatePrev) {
                const newIndex = currentIndex - 1;
                setCurrentIndex(newIndex);
                return contentList[newIndex]?.id ?? null;
            }
            if (direction === "next" && canNavigateNext) {
                const newIndex = currentIndex + 1;
                setCurrentIndex(newIndex);
                return contentList[newIndex]?.id ?? null;
            }
            return null;
        },
        [canNavigatePrev, canNavigateNext, currentIndex, contentList]
    );

    const value = useMemo(
        () => ({
            contentList,
            currentIndex,
            setContentList,
            clearContentList,
            navigateTo,
            canNavigatePrev,
            canNavigateNext,
            currentContent
        }),
        [
            contentList,
            currentIndex,
            setContentList,
            clearContentList,
            navigateTo,
            canNavigatePrev,
            canNavigateNext,
            currentContent
        ]
    );

    return (
        <ContentNavigationContext.Provider value={value}>
            {children}
        </ContentNavigationContext.Provider>
    );
};

export const useContentNavigation = () => useContext(ContentNavigationContext);
